/**
 * Group admin actions (Tier 3, 2026-09-29): invite link, "who can add members", join-request
 * approval, and leave-group. Rendered at the bottom of ChatInfoRail.jsx's GroupDescriptionSection,
 * so the desktop rail and the mobile/tablet GroupInfoDrawer both get it from one mount.
 *
 * Everything except "Leave group" is offered only when this workspace's own number is a group
 * admin (groupInfo.isSelfAdmin) -- WhatsApp rejects those from a non-admin, so the controls are
 * simply not shown rather than offered and refused. A real rejection still surfaces via snackbar.
 */
import React from "react";
import {
  Box, Button, CircularProgress, Divider, FormControl, MenuItem, Select, Snackbar, Alert, Typography,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, useTheme,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  fetchGroupInviteLink, resetGroupInviteLink, leaveWhatsappGroup,
  fetchGroupJoinRequests, respondGroupJoinRequests, setGroupMemberAddMode,
} from "../../redux/Slice/Inbox/inboxInfoSlice";
import { deleteGroupChat } from "../../redux/Slice/Inbox/inboxRecentSlice";
import connectionMonitor from "../../utils/connectionMonitor";
import { dispatchInboxSidebarTabChangeClear, navigateOffCustomerChatRoute } from "../../utils/inboxTabSwitch";

const errText = (e, fallback) => (typeof e === "string" ? e : fallback);

export default function GroupAdminActions() {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { accountid, userid } = useParams();
  const groupInfo = useSelector((state) => state.inboxInfo?.groupInfo);
  const groupId = groupInfo?.groupID;
  const isAdmin = !!groupInfo?.isSelfAdmin;

  const [link, setLink] = React.useState(null);
  const [linkBusy, setLinkBusy] = React.useState(false);
  const [requests, setRequests] = React.useState(null);
  const [requestsBusy, setRequestsBusy] = React.useState(false);
  const [modeBusy, setModeBusy] = React.useState(false);
  const [confirm, setConfirm] = React.useState(null); // "leave" | "resetLink" | null
  const [confirmBusy, setConfirmBusy] = React.useState(false);
  const [snack, setSnack] = React.useState({ open: false, message: "", severity: "success" });
  const notify = (message, severity = "success") => setSnack({ open: true, message, severity });

  const [hasNewRequest, setHasNewRequest] = React.useState(false);

  // A different group's link/requests must never linger when the open thread changes.
  React.useEffect(() => {
    setLink(null);
    setRequests(null);
    setHasNewRequest(false);
  }, [groupId]);

  // Live: the server pushes group_updated with reason "join_request" when someone asks to join,
  // cancels, or is rejected. If the pending list is already on screen, refresh it in place;
  // otherwise just flag that something is waiting so the admin knows to look.
  const requestsLoadedRef = React.useRef(false);
  requestsLoadedRef.current = requests !== null;
  React.useEffect(() => {
    if (!groupId) return undefined;
    const onGroupUpdated = async (payload) => {
      if (payload?.reason !== "join_request" || String(payload.groupId) !== String(groupId)) return;
      if (requestsLoadedRef.current) {
        try {
          const out = await dispatch(fetchGroupJoinRequests({ groupId })).unwrap();
          setRequests(out.requests || []);
        } catch (_) {
          /* keep the current list; the admin can press the button again */
        }
      } else {
        setHasNewRequest(true);
      }
    };
    connectionMonitor.on("group_updated", onGroupUpdated);
    return () => connectionMonitor.off("group_updated", onGroupUpdated);
  }, [dispatch, groupId]);

  if (!groupId) return null;

  const handleGetLink = async () => {
    setLinkBusy(true);
    try {
      const out = await dispatch(fetchGroupInviteLink({ groupId })).unwrap();
      setLink(out.link);
    } catch (e) {
      notify(errText(e, "Failed to get invite link"), "error");
    } finally {
      setLinkBusy(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      notify("Invite link copied");
    } catch {
      notify("Could not copy -- select the link and copy it manually", "error");
    }
  };

  const handleLoadRequests = async () => {
    setRequestsBusy(true);
    setHasNewRequest(false);
    try {
      const out = await dispatch(fetchGroupJoinRequests({ groupId })).unwrap();
      setRequests(out.requests || []);
    } catch (e) {
      notify(errText(e, "Failed to load join requests"), "error");
    } finally {
      setRequestsBusy(false);
    }
  };

  const handleRespond = async (jid, action) => {
    try {
      await dispatch(respondGroupJoinRequests({ groupId, body: { participants: [jid], action } })).unwrap();
      setRequests((prev) => (prev || []).filter((r) => r.jid !== jid));
      notify(action === "approve" ? "Request approved" : "Request rejected");
    } catch (e) {
      notify(errText(e, "Failed to update join request"), "error");
    }
  };

  const handleModeChange = async (mode) => {
    setModeBusy(true);
    try {
      await dispatch(setGroupMemberAddMode({ groupId, body: { mode } })).unwrap();
      notify("Setting updated");
    } catch (e) {
      notify(errText(e, "Failed to update setting"), "error");
    } finally {
      setModeBusy(false);
    }
  };

  const handleConfirm = async () => {
    setConfirmBusy(true);
    try {
      if (confirm === "resetLink") {
        const out = await dispatch(resetGroupInviteLink({ groupId })).unwrap();
        setLink(out.link);
        notify("Invite link reset -- the old link no longer works");
      } else if (confirm === "leave") {
        await dispatch(leaveWhatsappGroup({ groupId })).unwrap();
        // The server already hid the group (leftAt); this drops it from the loaded lists and
        // closes the open thread, same as the row's own "Delete Chat" does.
        await dispatch(deleteGroupChat(groupId)).unwrap().catch(() => {});
        dispatchInboxSidebarTabChangeClear(dispatch);
        navigateOffCustomerChatRoute(navigate, accountid, userid);
        notify("You left the group");
      }
      setConfirm(null);
    } catch (e) {
      notify(errText(e, "Action failed"), "error");
      setConfirm(null);
    } finally {
      setConfirmBusy(false);
    }
  };

  const heading = (text) => (
    <Typography sx={{ fontSize: "0.9375rem", fontWeight: 600, color: theme.palette.text.primary, mb: 1 }}>{text}</Typography>
  );
  const small = { fontSize: "0.8125rem", color: theme.palette.text.secondary };

  return (
    <Box>
      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
        <Alert severity={snack.severity} sx={{ width: "100%" }} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>

      {isAdmin && (
        <>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ pl: "18px", pr: "12px", py: 1.5 }}>
            {heading("Invite link")}
            {link ? (
              <>
                <Typography sx={{ ...small, wordBreak: "break-all", userSelect: "all", mb: 1 }}>{link}</Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button size="small" variant="outlined" onClick={handleCopy}>Copy</Button>
                  <Button size="small" color="warning" onClick={() => setConfirm("resetLink")}>Reset link</Button>
                </Box>
              </>
            ) : (
              <Button size="small" variant="outlined" onClick={handleGetLink} disabled={linkBusy}>
                {linkBusy ? <CircularProgress size={14} /> : "Show invite link"}
              </Button>
            )}
          </Box>

          <Divider sx={{ my: 1 }} />
          <Box sx={{ pl: "18px", pr: "12px", py: 1.5 }}>
            {heading("Who can add members")}
            <FormControl size="small" fullWidth disabled={modeBusy}>
              <Select
                displayEmpty
                value={groupInfo.memberAddMode || ""}
                onChange={(e) => handleModeChange(e.target.value)}
                renderValue={(v) => (v === "admin_add" ? "Only admins" : v === "all_member_add" ? "All members" : "Not set -- choose")}
              >
                <MenuItem value="admin_add">Only admins</MenuItem>
                <MenuItem value="all_member_add">All members</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Divider sx={{ my: 1 }} />
          <Box sx={{ pl: "18px", pr: "12px", py: 1.5 }}>
            {heading(hasNewRequest ? "Join requests • new" : "Join requests")}
            {requests === null ? (
              <Button size="small" variant="outlined" onClick={handleLoadRequests} disabled={requestsBusy}>
                {requestsBusy ? <CircularProgress size={14} /> : "Check pending requests"}
              </Button>
            ) : requests.length === 0 ? (
              <Typography sx={small}>No pending requests.</Typography>
            ) : (
              requests.map((r) => (
                <Box key={r.jid} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 0.75 }}>
                  <Typography sx={{ fontSize: "0.875rem", color: theme.palette.text.primary }}>{r.phoneNumberID || r.jid}</Typography>
                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    <Button size="small" variant="contained" onClick={() => handleRespond(r.jid, "approve")}>Approve</Button>
                    <Button size="small" color="error" onClick={() => handleRespond(r.jid, "reject")}>Reject</Button>
                  </Box>
                </Box>
              ))
            )}
          </Box>
        </>
      )}

      <Divider sx={{ my: 1 }} />
      <Box sx={{ pl: "18px", pr: "12px", py: 1.5 }}>
        <Button size="small" color="error" variant="outlined" onClick={() => setConfirm("leave")}>Leave group</Button>
      </Box>

      <Dialog open={!!confirm} onClose={confirmBusy ? undefined : () => setConfirm(null)}>
        <DialogTitle>{confirm === "leave" ? "Leave this group?" : "Reset invite link?"}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirm === "leave"
              ? "Your connected WhatsApp number will leave this group and it will disappear from your inbox. Existing chat history is kept."
              : "The current invite link will stop working and a new one will be created."}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)} disabled={confirmBusy}>Cancel</Button>
          <Button color="error" onClick={handleConfirm} disabled={confirmBusy}>
            {confirmBusy ? <CircularProgress size={16} /> : confirm === "leave" ? "Leave" : "Reset"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
